import { useState } from 'react';
import useSWR from 'swr';
import { adminApi, type SecretStatus } from '../../api/admin';

export function SecretsTab() {
  const { data, mutate } = useSWR('admin/secrets', () => adminApi.secretsStatus());
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!data) return <p className="text-sm text-cockpit-dim">loading…</p>;

  const dirty = Object.keys(edits).length > 0;
  const sortedKeys = Object.keys(data.status).sort();

  async function save() {
    if (!dirty) return;
    if (!confirm(`Update ${Object.keys(edits).length} secret(s) and restart the service?`)) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const res = await adminApi.updateSecrets(edits, true);
      setMsg(`Saved ${res.updated.length} key(s). Service is restarting — refresh in 5–10s.`);
      setEdits({});
      // Allow the restart to land before refetching
      setTimeout(() => void mutate(), 6000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-cockpit-dim">
            Secrets
          </h3>
          <p className="text-xs text-cockpit-dim">
            Stored in <code className="font-mono">{data.env_file}</code>. Saving restarts the
            service.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!dirty || busy}
            onClick={() => setEdits({})}
            className="rounded border border-cockpit-border px-3 py-1 text-xs text-cockpit-dim hover:text-cockpit-text disabled:opacity-40"
          >
            Reset
          </button>
          <button
            type="button"
            disabled={!dirty || busy}
            onClick={() => void save()}
            className="rounded bg-cockpit-text px-3 py-1 text-xs text-cockpit-bg disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Save & restart'}
          </button>
        </div>
      </div>
      {msg && (
        <div className="rounded border border-cockpit-ok bg-cockpit-ok/10 p-2 text-xs text-cockpit-ok">
          {msg}
        </div>
      )}
      {err && (
        <div className="rounded border border-cockpit-crit bg-cockpit-crit/10 p-2 text-xs text-cockpit-crit">
          {err}
        </div>
      )}

      <div className="overflow-hidden rounded border border-cockpit-border">
        <table className="w-full text-sm">
          <thead className="bg-cockpit-panel text-xs uppercase tracking-wider text-cockpit-dim">
            <tr>
              <th className="px-3 py-2 text-left">Key</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Value</th>
            </tr>
          </thead>
          <tbody>
            {sortedKeys.map((key) => {
              const status: SecretStatus = data.status[key]!;
              const editValue = edits[key];
              const editing = editValue !== undefined;
              return (
                <tr key={key} className="border-t border-cockpit-border">
                  <td className="px-3 py-2 font-mono text-xs">{key}</td>
                  <td className="px-3 py-2 text-xs">
                    {status.is_set ? (
                      <span className="text-cockpit-ok">configured</span>
                    ) : (
                      <span className="text-cockpit-warn">not set</span>
                    )}
                    {status.sensitive && <span className="ml-2 text-cockpit-dim">(masked)</span>}
                  </td>
                  <td className="px-3 py-2">
                    {status.sensitive ? (
                      <input
                        type="password"
                        autoComplete="new-password"
                        placeholder={
                          status.is_set ? '••••••••  (leave blank to keep)' : 'enter value'
                        }
                        value={editValue ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEdits((curr) => {
                            const next = { ...curr };
                            if (v === '') delete next[key];
                            else next[key] = v;
                            return next;
                          });
                        }}
                        className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-xs text-cockpit-text"
                      />
                    ) : (
                      <input
                        type="text"
                        value={editing ? editValue : (status.value ?? '')}
                        onChange={(e) => setEdits((curr) => ({ ...curr, [key]: e.target.value }))}
                        className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-xs text-cockpit-text"
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
